/* SPEC-506 — the feedback engine's pure logic: words, payload, redaction, the snip-first
 * flow, crop maths, the unread dot and the "since last call" default.
 *
 * No React, no DOM, no `@/` imports — so `node --experimental-strip-types` runs its tests
 * (core.test.ts) and the components stay thin. The server repeats the redaction
 * (routes/feedback_relay.py): this copy is the first line, not the only one. */

/* ── Words (planner wording; keys are the API contract) ──────────────────────── */

/* SPEC-521: 'scenario_idea' is filed from the Scenarios page, which opens the panel pre-set to it.
 * It is offered in the picker only when the panel was opened that way (see pickerCategories). */
export type FeedbackCategory = 'wrong' | 'broken' | 'question' | 'idea' | 'scenario_idea';

export interface CategoryCopy {
  key: FeedbackCategory;
  label: string;
  hint: string;
  prompt: string;
}

export const CATEGORIES: CategoryCopy[] = [
  { key: 'wrong', label: 'Something looks wrong', hint: "the numbers or screen don't look right", prompt: 'What looks wrong?' },
  { key: 'broken', label: 'Something is broken', hint: "it isn't working or shows an error", prompt: 'What happened?' },
  { key: 'question', label: 'I have a question', hint: 'about a number, a screen or how to do something', prompt: 'What would you like to know?' },
  { key: 'idea', label: 'I have an idea', hint: 'it would be useful if…', prompt: 'What would be useful?' },
  { key: 'scenario_idea', label: 'Scenario idea', hint: 'a what-if you would like to model', prompt: 'What scenario would you like to model?' },
];

/** The four everyday categories, always in the picker. */
const EVERYDAY: ReadonlySet<FeedbackCategory> = new Set<FeedbackCategory>(['wrong', 'broken', 'question', 'idea']);

/** The picker's buttons: the four everyday categories, plus a special one (a scenario idea)
 *  only when the panel was opened pre-set to it — it is not offered on every page. */
export function pickerCategories(selected: FeedbackCategory | null | undefined): CategoryCopy[] {
  return CATEGORIES.filter((c) => EVERYDAY.has(c.key) || c.key === selected);
}

export function categoryCopy(key: FeedbackCategory | null | undefined): CategoryCopy | undefined {
  return CATEGORIES.find((c) => c.key === key);
}

/** Wrong and Broken get the two optional follow-ups; questions and ideas do not. */
export function asksFollowUps(key: FeedbackCategory | null | undefined): boolean {
  return key === 'wrong' || key === 'broken';
}

/** The example under the text box, naming the page the reader is on. */
export function examplePrompt(key: FeedbackCategory, pageLabel: string | null): string {
  const where = pageLabel ? ` on ${pageLabel}` : '';
  switch (key) {
    case 'wrong': return `For example: "The stock cover${where} shows 12 weeks, but we have about 2."`;
    case 'broken': return `For example: "The download button${where} does nothing when I press it."`;
    case 'question': return `For example: "Where does the forecast${where} come from?"`;
    case 'scenario_idea': return `For example: "What if palm oil prices rise 20% next quarter — how would ${pageLabel ?? 'this screen'} change?"`;
    default: return `For example: "It would be useful if ${pageLabel ?? 'this screen'} showed weeks of cover."`;
  }
}

export const STATUS_LABELS: Record<string, string> = {
  received: 'Received',
  looking_into_it: 'Looking into it',
  need_more_from_you: 'Need more from you',
  fixed: 'Fixed',
  not_doing: 'Not doing',
  answered: 'Answered',
};

/** SPEC-527 — the triage form's ack body. Fields are sent as they stand ('' clears the stored
 *  value). `evidence` (what was checked) goes ONLY with "fixed" and only when it says something:
 *  the server refuses a "fixed" with neither a proved release nor evidence, and evidence left in
 *  the box after switching to another status must not be filed as a closing note. */
export function triageBody(status: string, reply: string, spec: string, evidence: string):
  { status: string; reply: string; changelog_spec: string; evidence?: string } {
  const body: { status: string; reply: string; changelog_spec: string; evidence?: string } =
    { status, reply: reply.trim(), changelog_spec: spec.trim() };
  if (status === 'fixed' && evidence.trim()) body.evidence = evidence.trim();
  return body;
}

export const ACK_COPY = 'Thanks, received. You can follow this under My feedback.';
export const PASTE_COPY = 'Paste a screenshot (Cmd/Ctrl+V) or choose a file';

/* ── The payload ─────────────────────────────────────────────────────────────── */

export interface FeedbackDraft {
  category: FeedbackCategory | null;
  text: string;
  expected: string;
  blocking: boolean | null;
  includeContext: boolean;
}

export const EMPTY_DRAFT: FeedbackDraft = {
  category: null, text: '', expected: '', blocking: null, includeContext: true,
};

export interface Where {
  page: string;
  tab: string | null;
  url: string;
  entity: { entity_type: string; entity_id: string } | null;
}

export function canSend(d: FeedbackDraft): boolean {
  return d.category !== null && d.text.trim().length > 0;
}

/** The JSON part of the multipart POST. Deliberately has NO severity / priority / steps
 *  field: the reader is never asked to rate how bad it is (SPEC-506).
 *  "Don't include technical details" sends the page PATH only — no query string (it can name a
 *  customer or a product), no selected entity, no context. The server enforces the same. */
/** What the browser checks against: the app's route templates and its known query parameters
 *  with their types — both served by /api/feedback/config (one source, shared with the server). */
export interface FeedbackSchema {
  routes: readonly string[];
  filterParams: Readonly<Record<string, string>>;
  /** The app's page routes ('/products/:code' style) — a page is stored as one of their templates. */
  pages: readonly string[];
}

export const EMPTY_SCHEMA: FeedbackSchema = { routes: [], filterParams: {}, pages: [] };

export function buildPayload(
  d: FeedbackDraft, where: Where, context: Record<string, unknown>, schema: FeedbackSchema = EMPTY_SCHEMA,
) {
  const follow = asksFollowUps(d.category);
  const share = d.includeContext;
  const page = cleanPage(where.page, schema.pages) ?? '/';
  return {
    category: d.category,
    text: d.text.trim(),
    expected: follow && d.expected.trim() ? d.expected.trim() : null,
    blocking: follow ? d.blocking : null,
    page,
    tab: where.tab,
    url: share ? (cleanUrl(where.url, schema.filterParams, schema.pages) ?? page) : page,
    entity: share ? where.entity : null,
    include_context: share,
    context: share ? cleanContext(context, schema) : null,
  };
}

/* ── Context schema (ALLOW-LIST by KEY and TYPE; mirrors routes/feedback_relay.py) ──
 * Fix loops 2-3 (Codex): no free text and no attacker-chosen value text. Every field is accepted
 * by its key (a fixed list) and its type (id / date / int / enum); anything else is dropped,
 * never inspected. Filters: only the app's known query parameters (served by the config), each by
 * type — free-text search boxes are never sent. Browser / platform / error type: enums. Feature
 * flags are NOT sent at all — the server stamps the platform's own. */

export const NEVER_KEY_PARTS = ['token', 'authorization', 'cookie', 'password', 'passwd', 'secret',
  'body', 'localstorage', 'sessionstorage', 'jwt', 'apikey', 'bearer', 'credential', 'html',
  'session', 'passcode', 'otp'];
/** Whole-key matches only. `code` is the sign-in callback; `q` / `search` are free-text boxes. */
export const NEVER_EXACT_KEYS = ['auth', 'sid', 'key', 'code', 'pin', 'pwd', 'ssn', 'q', 'search'];
const MAX_LIST = 10;
const ID_VALUE_RE = /^[A-Za-z0-9_-]{1,40}$/;
const CODE_VALUE_RE = /^[a-z0-9][a-z0-9_-]{0,29}$/;
const DATE_VALUE_RE = /^\d{4}-\d{2}-\d{2}$/;
const WEEK_VALUE_RE = /^\d{4}-W\d{2}$/;
const INT_VALUE_RE = /^\d{1,6}$/;
const TAB_RE = /^[a-z0-9][a-z0-9-]{0,59}$/;
const REQUEST_ID_RE = /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/;
const TABLE_RE = /^[a-z][a-z0-9_]{0,60}$/;
const STAMP_RE = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d{1,6})?)?)?(Z|[+-]\d{2}:\d{2})?$/;
const DIM_RE = /^\d{1,5}x\d{1,5}$/;
const LANG_RE = /^[a-z]{2,3}(-[A-Z]{2})?$/;
const TZ_RE = /^(UTC|[A-Za-z_]{1,20}(\/[A-Za-z0-9_+-]{1,30}){1,2})$/;
const VERSION_RE = /^\d{1,4}$/;
const METHODS = new Set(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']);
export const ERROR_TYPES = new Set(['Error', 'TypeError', 'RangeError', 'ReferenceError', 'SyntaxError',
  'URIError', 'EvalError', 'AggregateError', 'DOMException', 'AbortError', 'NetworkError', 'ChunkLoadError',
  'ConsoleMessage', 'ScriptError', 'UnhandledRejection', 'OtherError']);
export const BROWSERS = new Set(['chrome', 'safari', 'firefox', 'edge', 'other']);
export const PLATFORMS = new Set(['mac', 'windows', 'linux', 'ios', 'android', 'other']);
const PIPELINE_STATUSES = new Set(['success', 'completed', 'ok', 'running', 'failed', 'error', 'partial',
  'warning', 'skipped', 'unknown', 'unavailable']);

export function keyForbidden(key: string): boolean {
  const k = key.toLowerCase().replace(/[-_\s]/g, '');
  return NEVER_EXACT_KEYS.includes(k) || NEVER_KEY_PARTS.some((p) => k.includes(p));
}

// eslint-disable-next-line no-control-regex
const CTRL_RE = /[\u0000-\u001f\u007f]/;
/** The whole string must fit and no control character is ever accepted (fix loop 4). */
const match = (rx: RegExp, v: unknown): string | null =>
  (typeof v === 'string' && !CTRL_RE.test(v) && rx.test(v) ? v : null);
const intIn = (v: unknown, lo: number, hi: number): number | null =>
  (typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi ? v : null);
const numIn = (v: unknown, lo: number, hi: number): number | null =>
  (typeof v === 'number' && Number.isFinite(v) && v >= lo && v <= hi ? v : null);
/** Membership only after a type check. */
const member = (v: unknown, allowed: ReadonlySet<string>): string | null =>
  (typeof v === 'string' && allowed.has(v) ? v : null);

function typedValue(kind: string, v: unknown): string | null {
  switch (kind) {
    case 'id': return match(ID_VALUE_RE, v);
    case 'code': return match(CODE_VALUE_RE, v);
    case 'int': return match(INT_VALUE_RE, v);
    case 'date': return match(DATE_VALUE_RE, v);
    case 'week': return match(WEEK_VALUE_RE, v);
    case 'tab': return match(TAB_RE, v); // the server also checks it is a real registry tab
    default: return null;
  }
}

/** Only the known query parameters, each by its type; unknown keys dropped whatever their name. */
export function cleanFilters(raw: unknown, params: Readonly<Record<string, string>>): Record<string, string> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>).slice(0, 30)) {
    const kind = Object.prototype.hasOwnProperty.call(params, k) ? params[k] : undefined;
    if (!kind || keyForbidden(k)) continue;
    const val = typedValue(kind, v);
    if (val !== null) out[k] = val;
  }
  return out;
}

function compileRoute(spec: string): { template: string; rx: RegExp; params: number } {
  const segs = spec.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
  const rx: string[] = [];
  const tpl: string[] = [];
  let params = 0;
  for (const seg of segs) {
    if (seg === '*') { rx.push('(?:/.*)?'); break; }
    if (seg.startsWith(':')) { rx.push('/[^/]+'); tpl.push(`{${seg.slice(1)}}`); params += 1; }
    else { rx.push('/' + seg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')); tpl.push(seg); }
  }
  return { template: '/' + tpl.join('/'), rx: new RegExp(`^${rx.length ? rx.join('') : '/?'}$`), params };
}

/** Map a path to a KNOWN app route template, else the nearest top-level page by segment prefix,
 *  else '/'. A raw path is never sent (mirrors feedback_relay.clean_page). */
export function cleanPage(raw: unknown, pages: readonly string[] = []): string | null {
  if (typeof raw !== 'string') return null;
  let path = raw.split('?')[0].split('#')[0];
  if (!path.startsWith('/')) return null;
  if (CTRL_RE.test(path)) return '/';
  path = '/' + path.replace(/^\/+|\/+$/g, '');
  const compiled = pages.map(compileRoute).sort((a, b) => (a.params - b.params) || (b.template.length - a.template.length));
  const hit = compiled.find((c) => c.rx.test(path));
  if (hit) return hit.template;
  const statics = compiled.filter((c) => c.params === 0 && c.template !== '/').map((c) => c.template)
    .sort((a, b) => b.length - a.length);
  return statics.find((b) => path.startsWith(b + '/')) ?? '/';
}

/** The address rebuilt as page + the typed known filters. */
export function cleanUrl(
  raw: unknown, params: Readonly<Record<string, string>>, pages: readonly string[] = [],
): string | null {
  if (typeof raw !== 'string') return null;
  const page = cleanPage(raw, pages);
  if (page === null) return null;
  const q = raw.includes('?') ? raw.slice(raw.indexOf('?') + 1).split('#')[0] : '';
  const found: Record<string, string> = {};
  try { new URLSearchParams(q).forEach((v, k) => { found[k] = v; }); } catch { /* none */ }
  const qs = new URLSearchParams(cleanFilters(found, params)).toString();
  return page + (qs ? `?${qs}` : '');
}

function templateRegex(t: string): RegExp {
  const body = t.split('/').map((seg) => {
    const m = /^\{[^}:]+(:path)?\}$/.exec(seg);
    if (m) return m[1] ? '.+' : '[^/]+';
    return seg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }).join('/');
  return new RegExp(`^${body}$`);
}

/** The route template a request path belongs to, or null. Static templates win, then fewer params. */
export function routeTemplate(path: string, routes: readonly string[]): string | null {
  const ordered = [...routes].sort((a, b) => (a.split('{').length - b.split('{').length) || a.localeCompare(b));
  return ordered.find((t) => templateRegex(t).test(path)) ?? null;
}

function cleanApiCall(e: unknown, routes: ReadonlySet<string>): Record<string, unknown> | null {
  if (!e || typeof e !== 'object') return null;
  const r = e as Record<string, unknown>;
  const route = member(r.route, routes);
  const method = member(r.method, METHODS);
  if (!route || !method) return null;
  return {
    method, route,
    status: intIn(r.status, 100, 599),
    ms: intIn(r.ms, 0, 600_000),
    request_id: match(REQUEST_ID_RE, r.request_id),
  };
}

function cleanConsole(e: unknown): Record<string, unknown> | null {
  if (!e || typeof e !== 'object') return null;
  const r = e as Record<string, unknown>;
  if (typeof r.type !== 'string') return null;
  const count = intIn(r.count, 1, 1_000_000);
  return count ? { type: ERROR_TYPES.has(r.type) ? r.type : 'OtherError', count } : null;
}

function cleanFreshness(f: unknown): Record<string, unknown> | null {
  if (!f || typeof f !== 'object') return null;
  const r = f as Record<string, unknown>;
  if (r.unavailable === true) return { unavailable: true };
  const stale = Array.isArray(r.stale_tables) ? r.stale_tables.slice(0, 15) : [];
  const status = typeof r.pipeline_status === 'string' ? (PIPELINE_STATUSES.has(r.pipeline_status) ? r.pipeline_status : 'other') : null;
  return {
    pipeline_status: status,
    pipeline_last_run: match(STAMP_RE, r.pipeline_last_run),
    stale_tables: stale
      .filter((t): t is Record<string, unknown> => !!t && typeof t === 'object' && match(TABLE_RE, (t as Record<string, unknown>).table) !== null)
      .map((t) => ({ table: t.table, max_date: match(STAMP_RE, t.max_date), days_stale: intIn(t.days_stale, 0, 100_000) })),
    integrity_breaches: intIn(r.integrity_breaches, 0, 100_000),
  };
}

const enumOrOther = (v: unknown, allowed: ReadonlySet<string>): string | null =>
  (typeof v === 'string' ? (allowed.has(v.toLowerCase()) ? v.toLowerCase() : 'other') : null);

function cleanClient(c: unknown): Record<string, unknown> | null {
  if (!c || typeof c !== 'object') return null;
  const r = c as Record<string, unknown>;
  return {
    viewport: match(DIM_RE, r.viewport),
    screen: match(DIM_RE, r.screen),
    pixel_ratio: numIn(r.pixel_ratio, 0, 10),
    browser: enumOrOther(r.browser, BROWSERS),
    browser_version: match(VERSION_RE, r.browser_version),
    platform: enumOrOther(r.platform, PLATFORMS),
    language: match(LANG_RE, r.language),
    time_zone: match(TZ_RE, r.time_zone),
    utc_offset_minutes: intIn(r.utc_offset_minutes, -900, 900),
  };
}

/** Run one field cleaner; any unexpected shape drops the field. */
function guard<T>(fn: () => T): T | null {
  try { return fn(); } catch { return null; }
}

/** Build the context from the typed allow-list ONLY (mirrors the server). No flags. */
export function cleanContext(context: Record<string, unknown>, schema: FeedbackSchema = EMPTY_SCHEMA): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const routes = new Set(schema.routes);
  if ('filters' in context) {
    const f = guard(() => cleanFilters(context.filters, schema.filterParams));
    if (f) out.filters = f;
  }
  const fresh = guard(() => cleanFreshness(context.freshness));
  if (fresh) out.freshness = fresh;
  if (Array.isArray(context.api_calls)) {
    out.api_calls = context.api_calls.slice(0, MAX_LIST).map((e) => guard(() => cleanApiCall(e, routes))).filter((c) => c !== null);
  }
  if (Array.isArray(context.console_errors)) {
    out.console_errors = context.console_errors.slice(0, MAX_LIST).map((e) => guard(() => cleanConsole(e))).filter((c) => c !== null);
  }
  const client = guard(() => cleanClient(context.client));
  if (client) out.client = client;
  const stamp = match(STAMP_RE, context.captured_at);
  if (stamp) out.captured_at = stamp;
  return out;
}

/* ── Ring buffers for the last failed/slow requests ──────────────────────────── */

export function pushRing<T>(ring: T[], item: T, cap = 10): T[] {
  const next = [...ring, item];
  return next.length > cap ? next.slice(next.length - cap) : next;
}

/** One API call as recorded: method, ROUTE TEMPLATE, status, duration, request id. The raw
 *  path is mapped to a template at once and never kept; a path matching no template, and a
 *  call that is fine and fast, are not recorded (null). */
export function apiCallRecord(
  method: string, rawUrl: string, status: number | null, ms: number, requestId: string | null,
  routes: readonly string[], slowMs = 3000,
): { method: string; route: string; status: number | null; ms: number; request_id: string | null } | null {
  const failed = status === null || status >= 400;
  if (!failed && ms < slowMs) return null;
  let path: string;
  try { path = new URL(rawUrl, 'http://x').pathname; } catch { return null; }
  const route = routeTemplate(path, routes);
  if (!route) return null;
  return {
    method: method.toUpperCase(), route, status, ms: Math.round(ms),
    request_id: match(REQUEST_ID_RE, requestId),
  };
}

/** A console error reduced to its TYPE from a fixed list — no message text. */
export function errorType(arg: unknown): string {
  if (arg instanceof Error) return ERROR_TYPES.has(arg.name) ? arg.name : 'OtherError';
  return 'ConsoleMessage';
}

/** Browser family + major version from the user agent — never the agent string itself. */
export function browserOf(ua: string): { browser: string; browser_version: string | null } {
  const order: Array<[string, RegExp]> = [
    ['edge', /Edg\/(\d+)/], ['firefox', /Firefox\/(\d+)/], ['chrome', /Chrome\/(\d+)/],
    ['safari', /Version\/(\d+)[^ ]* Safari/],
  ];
  for (const [name, rx] of order) {
    const m = rx.exec(ua);
    if (m) return { browser: name, browser_version: m[1].slice(0, 4) };
  }
  return { browser: 'other', browser_version: null };
}

/** Platform as an enum. */
export function platformOf(raw: string): string {
  const p = raw.toLowerCase();
  if (p.includes('iphone') || p.includes('ipad') || p === 'ios') return 'ios';
  if (p.includes('mac')) return 'mac';
  if (p.includes('win')) return 'windows';
  if (p.includes('android')) return 'android';
  if (p.includes('linux')) return 'linux';
  return 'other';
}

/** Counts per type, newest types kept (at most `cap` distinct types). */
export function countType(counts: Array<{ type: string; count: number }>, type: string, cap = 10) {
  const hit = counts.find((c) => c.type === type);
  if (hit) return counts.map((c) => (c.type === type ? { type, count: c.count + 1 } : c));
  return pushRing(counts, { type, count: 1 }, cap);
}

/* ── The snip-first flow ─────────────────────────────────────────────────────── */

export type Phase = 'closed' | 'capturing' | 'snip' | 'markup' | 'panel';

export interface Shot { url: string; width: number; height: number; source: 'capture' | 'paste' | 'file' }

export interface FlowState {
  phase: Phase;
  /** The full-page capture, while snipping. */
  capture: Shot | null;
  /** The image that will be attached. */
  shot: Shot | null;
  captureFailed: boolean;
}

export const INITIAL_FLOW: FlowState = { phase: 'closed', capture: null, shot: null, captureFailed: false };

export type FlowAction =
  | { type: 'open'; autoCapture: boolean }
  | { type: 'captured'; capture: Shot }
  | { type: 'capture_failed' }
  | { type: 'snipped'; shot: Shot }
  | { type: 'markup' }
  | { type: 'markup_done'; shot: Shot }
  | { type: 'markup_cancel' }
  | { type: 'skip_shot' }
  | { type: 'set_shot'; shot: Shot }
  | { type: 'remove_shot' }
  | { type: 'retake' }
  | { type: 'close' };

/** The panel only ever renders in phase 'panel'. Capturing happens in 'capturing', before
 *  it exists — that ordering is what keeps the panel out of its own screenshot. */
export function flowReducer(s: FlowState, a: FlowAction): FlowState {
  switch (a.type) {
    case 'open':
      if (s.phase !== 'closed') return s;
      return a.autoCapture
        ? { ...INITIAL_FLOW, phase: 'capturing' }
        : { ...INITIAL_FLOW, phase: 'panel' };
    case 'captured':
      return s.phase === 'capturing' ? { ...s, phase: 'snip', capture: a.capture } : s;
    case 'capture_failed':
      return s.phase === 'capturing' ? { ...s, phase: 'panel', captureFailed: true } : s;
    case 'snipped':
      return { ...s, phase: 'panel', capture: null, shot: a.shot };
    case 'markup':
      return s.shot ? { ...s, phase: 'markup' } : s;
    case 'markup_done':
      return { ...s, phase: 'panel', shot: a.shot };
    case 'markup_cancel':
      return { ...s, phase: 'panel' };
    case 'skip_shot':
      return { ...s, phase: 'panel', capture: null, shot: null };
    case 'set_shot':
      return { ...s, shot: a.shot };
    case 'remove_shot':
      return { ...s, shot: null };
    case 'retake':
      return { ...s, phase: 'capturing', capture: null };
    case 'close':
      return INITIAL_FLOW;
    default:
      return s;
  }
}

/** Object URLs a transition leaves behind (to revoke), so a long session does not leak blobs. */
export function droppedUrls(before: FlowState, after: FlowState): string[] {
  const live = new Set([after.capture?.url, after.shot?.url].filter(Boolean));
  return [before.capture?.url, before.shot?.url].filter((u): u is string => !!u && !live.has(u));
}

/* ── Crop and scale maths ────────────────────────────────────────────────────── */

export interface Pt { x: number; y: number }
export interface Rect { x: number; y: number; w: number; h: number }

/** A drag on the DISPLAYED image → the rectangle in the image's NATURAL pixels, clamped to
 *  the image. Returns null for a drag too small to mean anything (a click), which callers
 *  treat as "use the whole page". */
export function cropRect(
  a: Pt, b: Pt, displayW: number, displayH: number, naturalW: number, naturalH: number, minPx = 8,
): Rect | null {
  if (displayW <= 0 || displayH <= 0) return null;
  const clampX = (v: number) => Math.min(Math.max(v, 0), displayW);
  const clampY = (v: number) => Math.min(Math.max(v, 0), displayH);
  const x0 = clampX(Math.min(a.x, b.x)), x1 = clampX(Math.max(a.x, b.x));
  const y0 = clampY(Math.min(a.y, b.y)), y1 = clampY(Math.max(a.y, b.y));
  if (x1 - x0 < minPx || y1 - y0 < minPx) return null;
  const sx = naturalW / displayW, sy = naturalH / displayH;
  const x = Math.round(x0 * sx), y = Math.round(y0 * sy);
  return {
    x, y,
    w: Math.min(naturalW - x, Math.round((x1 - x0) * sx)),
    h: Math.min(naturalH - y, Math.round((y1 - y0) * sy)),
  };
}

/** Scale (w, h) down to fit within `max` on the longest side; never scales up. */
export function fitWithin(w: number, h: number, max: number): { w: number; h: number } {
  const longest = Math.max(w, h);
  if (longest <= max || longest <= 0) return { w, h };
  const k = max / longest;
  return { w: Math.max(1, Math.round(w * k)), h: Math.max(1, Math.round(h * k)) };
}

/** The two barb points of an arrowhead at `to`, pointing away from `from`. */
export function arrowHead(from: Pt, to: Pt, size: number): [Pt, Pt] {
  const ang = Math.atan2(to.y - from.y, to.x - from.x);
  const spread = Math.PI / 7;
  return [
    { x: to.x - size * Math.cos(ang - spread), y: to.y - size * Math.sin(ang - spread) },
    { x: to.x - size * Math.cos(ang + spread), y: to.y - size * Math.sin(ang + spread) },
  ];
}

/* ── My feedback: the unread dot ─────────────────────────────────────────────── */

export interface MineItem { id: string; status: string }

/** Reports whose status changed since the reader last opened My feedback. A report the
 *  reader has never seen counts only once it has moved past "received" — filing one
 *  yourself is not news. */
export function unreadIds(items: MineItem[], seen: Record<string, string>): string[] {
  return items
    .filter((i) => (i.id in seen ? seen[i.id] !== i.status : i.status !== 'received'))
    .map((i) => i.id);
}

export function markSeen(items: MineItem[]): Record<string, string> {
  return Object.fromEntries(items.map((i) => [i.id, i.status]));
}

/* ── Operator triage: "since last call" ──────────────────────────────────────── */

/** The default "last call" date: the most recent Friday on or before today (the team call
 *  is weekly on Fridays). Returned as YYYY-MM-DD in local time. The operator can change it. */
export function lastCallDefault(now: Date): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const back = (d.getDay() - 5 + 7) % 7;
  d.setDate(d.getDate() - back);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** Capture filter: anything marked as part of the feedback UI is left out of the picture. */
export const FEEDBACK_UI_ATTR = 'data-feedback-ui';

export function excludedFromCapture(node: { nodeType: number; getAttribute?: (n: string) => string | null }): boolean {
  return node.nodeType === 1 && typeof node.getAttribute === 'function'
    && node.getAttribute(FEEDBACK_UI_ATTR) !== null;
}

/* ── Opening the panel from elsewhere ────────────────────────────────────────── */

/** The window event any surface fires to open Feedback (SPEC-509's rail button fires it with
 *  `{ source: 'rail' }`). One event name, defined once. */
export const OPEN_FEEDBACK_EVENT = 'suryaiq:open-feedback';

export interface OpenFeedbackDetail {
  source?: string;
  category?: FeedbackCategory;
  text?: string;
  tab?: 'new' | 'mine';
}

const KNOWN_CATEGORIES = new Set<string>(['wrong', 'broken', 'question', 'idea', 'scenario_idea']);

/** Listen for OPEN_FEEDBACK_EVENT on `target`; returns the unsubscribe. Unknown detail fields
 *  (or a missing detail) open the default flow rather than being trusted. */
export function subscribeOpenFeedback(target: EventTarget, open: (d: OpenFeedbackDetail) => void): () => void {
  const handler = (e: Event) => {
    const raw = ((e as CustomEvent).detail ?? {}) as Record<string, unknown>;
    const d: OpenFeedbackDetail = {};
    if (typeof raw.source === 'string') d.source = raw.source;
    if (typeof raw.category === 'string' && KNOWN_CATEGORIES.has(raw.category)) d.category = raw.category as FeedbackCategory;
    if (typeof raw.text === 'string') d.text = raw.text.slice(0, 4000);
    if (raw.tab === 'mine' || raw.tab === 'new') d.tab = raw.tab;
    open(d);
  };
  target.addEventListener(OPEN_FEEDBACK_EVENT, handler);
  return () => target.removeEventListener(OPEN_FEEDBACK_EVENT, handler);
}

/** SPEC-457's "Request a change?" seam: the pre-fill params in the URL, as open options plus
 *  the search string with them stripped (consumed once). Null when there is no pre-fill. */
export function consumePrefill(search: string, titleKey: string, descKey: string):
  { open: OpenFeedbackDetail; rest: string } | null {
  const params = new URLSearchParams(search);
  const title = params.get(titleKey);
  const desc = params.get(descKey);
  if (title === null && desc === null) return null;
  params.delete(titleKey);
  params.delete(descKey);
  // A thumbs-down on a figure is "Something looks wrong"; the reader can change it.
  return { open: { category: 'wrong', text: [title, desc].filter(Boolean).join('\n') }, rest: params.toString() };
}
